import styled, { css } from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

export const pageFade = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.35, ease: 'easeOut' } },
  exit: { opacity: 0, transition: { duration: 0.15 } },
};

// Used by app-style pages (Shop, Auth, Dashboard, Analytics).
export const PageMain = styled(motion.main)`
  min-height: calc(100vh - var(--header-height));
  padding: calc(var(--header-height) + 2.25rem) 1.5rem 4.5rem;
  max-width: var(--page-max-width);
  margin: 0 auto;
  width: 100%;
  position: relative;
`;

// Marketing pages: full-width, sections handle their own container.
export const Page = styled(motion.main).attrs({
  variants: pageFade,
  initial: 'initial',
  animate: 'animate',
  exit: 'exit',
})`
  padding-top: var(--header-height);
  width: 100%;
`;

export const Container = styled.div`
  max-width: var(--page-max-width);
  margin: 0 auto;
  padding: 0 1.5rem;
`;

export const Section = styled.section`
  padding: 7rem 0;

  ${(p) =>
    p.$band &&
    css`
      background: var(--surface-1);
      border-top: 1px solid var(--border-subtle);
      border-bottom: 1px solid var(--border-subtle);
    `}
  ${(p) => p.$ruled && css`border-top: 1px solid var(--border-subtle);`}
  ${(p) => p.$tight && css`padding-top: 0;`}
`;

export const Mono = styled.span`
  font-family: var(--font-mono);
  font-size: 0.75rem;
  color: ${(p) => (p.$dim ? 'var(--text-tertiary)' : 'var(--text-secondary)')};
`;

export const Eyebrow = styled.span`
  display: block;
  font-family: var(--font-mono);
  font-size: 0.8125rem;
  color: var(--brand);
  margin-bottom: 0.875rem;
`;

// App pages (Dashboard, Analytics).
export const PageHeading = styled.h1`
  font-size: clamp(2.2rem, 4.8vw, 3.25rem);
  line-height: 1.1;
  letter-spacing: -0.03em;
  margin-bottom: 1rem;
`;

// Marketing page hero headline.
export const Display = styled.h1`
  font-size: clamp(2.5rem, 5.5vw, 4.25rem);
  line-height: 1.04;
  letter-spacing: -0.035em;
  margin: 0 0 1.5rem;
  max-width: 56rem;
`;

export const SectionTitle = styled.h2`
  font-size: clamp(2rem, 4vw, 3rem);
  line-height: 1.08;
  letter-spacing: -0.03em;
  margin: 0 0 1rem;
  max-width: 47.5rem;
`;

export const Lead = styled.p`
  font-size: 1.1875rem;
  max-width: 40rem;
`;

export const Prose = styled.p`
  max-width: 38.75rem;
  margin-bottom: 3.5rem;
`;

export const Card = styled.div`
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 0.625rem;
  overflow: hidden;
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, ${(p) => p.$min || '20rem'}), 1fr));
  gap: ${(p) => p.$gap || '1.25rem'};
`;

// Small label/value list (About, Contact).
export const Facts = styled.dl`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(9.5rem, 1fr));
  gap: 1.5rem;
  border-top: 1px solid var(--border);
  padding-top: 1.75rem;

  dt {
    font-family: var(--font-mono);
    font-size: 0.75rem;
    color: var(--text-tertiary);
    margin-bottom: 0.375rem;
  }

  dd {
    font-weight: 500;
  }
`;

export const Tag = styled.span`
  font-family: var(--font-mono);
  font-size: ${(p) => (p.$small ? '0.6875rem' : '0.8125rem')};
  padding: ${(p) => (p.$small ? '0.25rem 0.5625rem' : '0.4375rem 0.75rem')};
  border: 1px solid var(--border-accent);
  border-radius: ${(p) => (p.$small ? '0.25rem' : '0.375rem')};
  color: var(--text-code);
  white-space: nowrap;
`;

const buttonBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.625rem;
  min-height: 2.75rem;
  padding: 0.875rem 1.5rem;
  border-radius: 0.375rem;
  font-weight: 600;
  font-size: 1rem;
  line-height: 1.2;
  transition: background var(--transition-fast), border-color var(--transition-fast), color var(--transition-fast);
`;

// Pass `href` (with as="a") for external links, `to` for routes.
export const PrimaryButton = styled(Link)`
  ${buttonBase}
  background: var(--brand);
  color: var(--brand-ink);

  &:hover {
    background: #ff8f4f;
  }
`;

export const GhostButton = styled(Link)`
  ${buttonBase}
  font-weight: 500;
  border: 1px solid var(--border-accent);
  color: var(--text-primary);

  &:hover {
    border-color: var(--text-tertiary);
  }
`;

export const TextLink = styled(Link)`
  font-size: 0.9375rem;
  font-weight: 500;
  padding: 0.75rem 0;
  border-bottom: 1px solid var(--border-accent);
  transition: color var(--transition-fast), border-color var(--transition-fast);

  &:hover {
    color: var(--brand);
    border-color: var(--brand);
  }
`;

export const Check = ({ accent }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke={accent ? 'var(--brand)' : 'var(--text-secondary)'}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    style={{ flexShrink: 0, marginTop: 3 }}
  >
    <path d="M5 12l5 5L20 7" />
  </svg>
);

export const CheckList = styled.ul`
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
  font-size: 0.9375rem;

  li {
    display: flex;
    gap: 0.75rem;
  }
`;

const CtaBox = styled(Card)`
  padding: clamp(2.25rem, 6vw, 4.5rem);
  border-radius: 0.75rem;
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
  align-items: center;
  justify-content: space-between;

  h2 {
    font-size: clamp(1.875rem, 4vw, 2.75rem);
    line-height: 1.1;
    letter-spacing: -0.03em;
    margin-bottom: 0.875rem;
  }

  p {
    max-width: 32.5rem;
  }
`;

const CtaActions = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

// `spaced` adds top padding when the CTA follows a full-bleed band instead of a regular section.
export const CtaBand = ({ title, text, spaced, children }) => (
  <Section $tight={!spaced}>
    <Container>
      <CtaBox>
        <div style={{ flex: '1 1 30rem', minWidth: 0 }}>
          <h2>{title}</h2>
          <p>{text}</p>
        </div>
        <CtaActions>{children}</CtaActions>
      </CtaBox>
    </Container>
  </Section>
);

export const DISCORD_DM = 'https://discord.com/users/skeered';
export const DISCORD_SERVER = 'https://discord.gg/rVCFxVpf29';
